import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as Yup from 'yup';

import WebSocket from '../../../api/web-socket/WebSocket';
import ErrorPanel from '../../../common-components/error-components/error-panel/error-panel';
import LoadingPanel from '../../../common-components/loading-panel/loading-panel';
import SimpleSearchBar from '../../../common-components/simple-search-bar/simple-search-bar';
import { useMobile } from '../../../hooks';
import {
  validateLatitudeDms,
  validateLongitudeDms,
} from '../../../utils/validation-helpers.utils';
import useNearbyVessels from './hooks/use-nearby-vessels';
import NearbyVesselsFormSummary from './nearby-vessels-form-summary/nearby-vessels-form-summary';
import NearbyVesselsForm from './nearby-vessels-form/nearby-vessels-form';
import { defaultValues } from './nearby-vessels-form/nearby-vessels-form-values';
import NearbyVesselsResults from './nearby-vessels-results/nearby-vessels-results';

const validationSchema = Yup.object().shape({
  radius: Yup.number()
    .typeError('Radius must be a number')
    .required('Radius is required')
    .max(50, 'Radius must not exceed 50nm'),
  timeSinceLastAIS: Yup.number()
    .typeError('Time since last AIS ping must be a number')
    .required('Time since last AIS ping is required')
    .max(24, 'Time since last AIS ping must not exceed 24 hours'),
  centrePoint: Yup.object().shape({
    latitude: validateLatitudeDms(),
    longitude: validateLongitudeDms(),
  }),
});

function NearbyVessels() {
  const {
    vessels,
    loading,
    error,
    formValues,
    onFormValuesChange,
    onSubmit,
    onReset,
    onCancel,
    longWaitMessage,
    pendingCount,
    websocketProps,
  } = useNearbyVessels();

  const [formExpanded, setFormExpanded] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const isMobile = useMobile();

  const formik = useFormik({
    initialValues: { ...formValues },
    // Wrap in an async function so formik always receives a Promise and reliably
    // resets isSubmitting to false once the submit handler completes — regardless
    // of whether the underlying hook implementation is sync or async.
    onSubmit: async (values) => {
      await onSubmit(values);
    },
    validationSchema,
  });

  const handleResetForm = () => {
    formik.resetForm();
    formik.setValues(defaultValues);
    onReset();
  };

  // Collapse the form the first time results arrive; re-expand when cleared.
  const prevVesselsCountRef = useRef(0);
  useEffect(() => {
    if (vessels.length === 0) {
      setFormExpanded(true);
    } else if (prevVesselsCountRef.current === 0) {
      setFormExpanded(false);
    }
    prevVesselsCountRef.current = vessels.length;
  }, [vessels.length]);

  const filteredVessels = useMemo(() => {
    if (searchFilter === '') return vessels;
    const term = searchFilter.toLowerCase();
    return vessels.filter(
      (v) =>
        v.name?.toLowerCase().includes(term) ||
        String(v.mmsi).includes(term) ||
        String(v.imo).toLowerCase().includes(term)
    );
  }, [vessels, searchFilter]);

  const hasResults = vessels.length > 0;
  let formBoxHeight = formExpanded ? '50%' : '10%';
  let resultsBoxHeight = formExpanded ? '50%' : '90%';
  if (isMobile) {
    formBoxHeight = 'auto';
    resultsBoxHeight = '60vh';
  }

  return (
    <Box
      data-testid="nearby-vessels"
      sx={{
        overflowY: isMobile ? 'auto' : 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '92vh',
      }}
    >
      {loading && <LoadingPanel />}
      {loading && pendingCount && pendingCount >= 1 && (
        <Typography sx={{ textAlign: 'center', mt: 1 }}>
          {pendingCount} vessel{pendingCount !== 1 ? 's' : ''} found so far…
        </Typography>
      )}
      {loading && longWaitMessage && (
        <Typography sx={{ textAlign: 'center' }}>{longWaitMessage}</Typography>
      )}
      {loading && onCancel && (
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            height: '5vh',
            marginTop: '10px',
          }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ width: '80%', height: '80%', margin: '1rem', flex: 'none' }}
          >
            Cancel Search
          </Button>
        </Grid>
      )}
      {error && <ErrorPanel message="Failed to load nearby vessels" />}
      {!loading && (
        <Box
          sx={{
            height: formBoxHeight,
            overflowY: isMobile ? 'visible' : 'hidden',
          }}
        >
          {hasResults && (
            <IconButton
              onClick={() => setFormExpanded((prev) => !prev)}
              sx={{ float: 'right' }}
            >
              {formExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
          {formExpanded ? (
            <NearbyVesselsForm
              formik={formik}
              handleReset={handleResetForm}
              onValuesChange={onFormValuesChange}
            />
          ) : (
            <NearbyVesselsFormSummary formValues={formValues} />
          )}
        </Box>
      )}

      {hasResults && !loading && !error && (
        <Box
          sx={{
            overflowY: 'hidden',
            fontSize: 'small',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            height: resultsBoxHeight,
            paddingBottom: '10px',
          }}
        >
          <Divider style={{ margin: '1rem 0' }} />
          <Box sx={{ display: 'flex', padding: '0px 20px' }}>
            <SimpleSearchBar
              handleChange={(value: string) => setSearchFilter(value)}
              filterValue={searchFilter}
              placeholder="Search Nearby Vessels"
            />
          </Box>
          {filteredVessels.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  marginTop: '1rem',
                  width: '100%',
                }}
              >
                <NearbyVesselsResults results={filteredVessels} />
              </Box>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  height: '5vh',
                  marginTop: '10px',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleResetForm}
                  sx={{
                    width: '30%',
                    height: '80%',
                    marginRight: '1rem',
                    flex: 'none',
                  }}
                >
                  Reset
                </Button>
              </Grid>
            </Box>
          ) : (
            <Typography sx={{ textAlign: 'center' }}>
              No results found. Please try a different search term.
            </Typography>
          )}
        </Box>
      )}

      {websocketProps && (
        <WebSocket
          url={websocketProps.url}
          signUrl={websocketProps.signUrl}
          onMessageReceived={websocketProps.onMessageReceived}
          onConnectionOpened={websocketProps.onConnectionOpened}
          onConnectionClosed={websocketProps.onConnectionClosed}
          onConnectionError={websocketProps.onConnectionError}
        />
      )}
    </Box>
  );
}

export default NearbyVessels;
